import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { IAuthSession } from '@redngapps/feature-flags/data-access';
import { isNil } from 'lodash-es';
import { catchError, exhaustMap, filter, map, of, switchMap, tap } from 'rxjs';

import { AUTH_FORBIDDEN_MESSAGE } from '../auth-forbidden-message.const';
import { AuthService } from '../services/auth.service';
import { toAuthErrorMessage } from '../to-auth-error-message';

import { login, loginFailure, loginSuccess, logout, logoutFailure, logoutSuccess } from './feature-flags-auth.actions';

@Injectable()
export class FeatureFlagsAuthEffects {
  private readonly actions$ = inject(Actions);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);

  login$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(login),
      exhaustMap(() =>
        this.authService.login().pipe(
          // The OIDC provider redirects away and emits null; the dev-token provider resolves a
          // session here. A token only authenticates — resolve the user's read/write permissions,
          // which gate access: without `read` the user is treated as not authorized and bounced
          // back to the login page with the reason instead of entering the app.
          filter((session): session is IAuthSession => !isNil(session)),
          switchMap(session =>
            this.authService
              .getPermissions(session.accessToken)
              .pipe(
                map(permissions =>
                  permissions.read
                    ? loginSuccess({ session, permissions })
                    : loginFailure({ error: AUTH_FORBIDDEN_MESSAGE }),
                ),
              ),
          ),
          catchError(error => of(loginFailure({ error: toAuthErrorMessage(error) }))),
        ),
      ),
    );
  });

  loginSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(loginSuccess),
        tap(() => {
          this.router.navigate(['/uebersicht']);
        }),
      );
    },
    { dispatch: false },
  );

  logout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(logout),
      exhaustMap(() =>
        this.authService.logout().pipe(
          map(() => logoutSuccess()),
          catchError(error => of(logoutFailure({ error: error?.message ?? 'Abmeldung fehlgeschlagen' }))),
        ),
      ),
    );
  });

  logoutSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(logoutSuccess),
        tap(() => {
          this.router.navigate(['/auth/login']);
        }),
      );
    },
    { dispatch: false },
  );
}
